(ns hai.home.desktop.mini-apps.notes
  "My Notes mini-app card: the user's latest notes as a list
   ([[hai.notes.recent]]). ＋ swaps the card body for the shared note editor on
   a blank draft and a row swaps it for the editor on that note; while it is
   open the header retitles and holds ➤, ⋮, ⤢ and ✕, with ⤢ opening the same
   draft in the roomier modal. The header's looking glass opens the universal
   bar; the title links to the full Notes page. Everything shown comes from
   [[hai.notes.mini-app]]; this card brings the frame, the sizes, the routing
   and the modal."
  (:require
   [hai.entity.data :as entity-data]
   [hai.learn-more :as learn-more]
   [hai.mini-apps.data :as mini-apps-data]
   [hai.notes.editor :as editor]
   [hai.notes.mini-app :as notes-mini-app]
   [hai.notes.recent :as recent]
   [hai.sharing.helper :as sharing]
   [hai.spaces.data :as spaces-data]
   [hai.ui.rf-sugar :refer [<s >e]]
   [hai.web-app.routing :as routing]
   [hai.web-app.universal-bar :as universal-bar]
   [lambdaisland.ornament :as o]
   [reagent.core :as r]
   [url.how-it-works :as how-it-works]))

;; Links the add/save toasts to the note. Home lists notes rather than showing
;; one, so the link always has somewhere to go.
(def ^:private note-link
  {:href-fn #(routing/href :notes/detail {:note/uuid %})
   :target routing/link-target})

;; The card is a fixed height either way: the list scrolls inside it, and the
;; editor's textarea flexes and scrolls under the chip rows and footer.
(o/defstyled card notes-mini-app/card
  [notes-mini-app/list-body {:max-height "440px"}]
  [notes-mini-app/editor-pane {:height "320px"}])

;; The pane and the modal edit the one draft, so ⤢ only has to open the dialog
;; and its ✕ only has to close it — what is written stays where it is either
;; way, and Cancel puts both back to the note as last saved.
(defn- editing-view [{:keys [note]}]
  (r/with-let [!modal-open? (r/atom false)
               !pane-el (atom nil)
               ;; Leaving the expanded editor puts the writer back in the pane
               ;; mid-sentence, so the text takes the focus rather than the
               ;; browser dropping it on the body.
               focus-text! (fn []
                             (when-let [^js el @!pane-el]
                               (some-> (.querySelector el "textarea.body") (.focus))))
               close-modal! (fn [] (reset! !modal-open? false))]
    (let [modal-open? @!modal-open?
          can-edit? (notes-mini-app/editable? note)
          editor-props {:note            note
                        :draft-id        recent/draft-id
                        :spaces?         @(<s [::spaces-data/enabled?])
                        :saving?         (= :start @(<s [:fetch/state ::recent/save]))
                        :show-hint?      true
                        :on-save         #(>e [::recent/save % {:source :web-app :note-link note-link}])
                        :on-cancel       #(>e [::recent/cancel])
                        :on-open-entity  entity-data/open-entity
                        :on-share-closed #(>e [::recent/refresh-editing])}]
      [:<>
       [notes-mini-app/editing-header
        {:note            note
         :on-close        #(>e [::recent/close-editor])
         :on-expand       #(reset! !modal-open? true)
         :on-delete       #(>e [::recent/delete])
         :on-share-closed #(>e [::recent/refresh-editing])}]
       [notes-mini-app/editor-view
        (assoc editor-props
               :pane-ref #(reset! !pane-el %)
               :auto-focus? true)]
       [editor/dialog
        ;; Radix would restore the focus to whatever had it before the dialog
        ;; opened — the ⤢ button — so it is taken over here.
        {:open?               modal-open?
         :on-close            close-modal!
         :on-close-auto-focus (fn [^js e]
                                (.preventDefault e)
                                (focus-text!))
         :title               (notes-mini-app/editor-title note)}
        (when modal-open?
          [editor/modal-content
           (assoc editor-props
                  :title (notes-mini-app/editor-title note)
                  :editing? can-edit?
                  :share? (sharing/can-share? note)
                  :on-close close-modal!
                  :on-delete (fn []
                               (close-modal!)
                               (>e [::recent/delete])))])]])))

(defn panel [{title :mini-app/title}]
  (let [collapsed? @(<s [::mini-apps-data/collapsed? :notes])
        loaded? @(<s [::recent/loaded?])
        notes @(<s [::recent/list])
        editing-note @(<s [::recent/editing-note])]
    (if editing-note
      [card
       [editing-view {:note editing-note}]]
      [card {:class (when collapsed? "collapsed")}
       [notes-mini-app/list-header
        {:title               title
         :href                (routing/href :notes/view)
         :target              routing/link-target
         :collapsed?          collapsed?
         :on-toggle-collapsed #(>e [::mini-apps-data/toggle-collapsed :notes])
         :on-search           #(>e [::universal-bar/open])
         :on-add              (fn []
                                (>e [::mini-apps-data/expand :notes])
                                (>e [::recent/create]))
         :menu                [learn-more/menu {:url how-it-works/notes-url :label "Notes"}]}]
       (when-not collapsed?
         (if loaded?
           [notes-mini-app/note-list
            {:notes          notes
             :on-open        #(>e [::recent/edit %])
             :on-open-entity entity-data/open-entity
             :see-all        {:href (routing/href :notes/view) :target routing/link-target}
             :empty-text     "Nothing yet — add your first note above."}]
           [notes-mini-app/list-loading]))])))
